import React, { useEffect, useMemo, useState } from 'react'
import { stringify } from 'querystring'
import RadarChart from 'Components/radar-chart'
import Indicator from './indicator'
import Stars from 'Components/stars'
import Bar from 'Components/bar'
import { IAuthor } from 'Components/author'
import SeriesRank, { IRankItem } from './series-rank'
import classNames from 'classnames'
import fetch from 'lib/decorated-fetch'

const dcar_score_icon = require('static/dcar-score.png')
const star_icon = require('static/star-bling.png')
const star_modest_icon = require('static/star-modest.png')
const wave_point_icon = require('static/wave-point.png')
interface IScoreInfoItem {
  name: string
  value: number
}
interface IReviewCountInfo {
  range1: number
  range2: number
  range3: number
  range4: number
  range5: number
  total: number
}

export default function DCarScore({
  series_id,
  className,
}: {
  series_id: number
  className?: string
}) {
  const [scoreInfo, setScoreInfo] = useState<IScoreInfoItem[]>([])
  const [score, setScore] = useState(0)
  const [reviewCountInfo, setReviewCountInfo] = useState<IReviewCountInfo>()
  const [authors, setAuthors] = useState<IAuthor[]>([])
  useEffect(() => {
    if (!series_id) {
      return
    }
    const fetchData = async () => {
      const query = {
        series_id,
        data_from: 'pc_station',
      }

      const json = await fetch(
        `/motor/car_score/api/m/v1/landing_page/get_detail/?${stringify(query)}`
      )
      setScoreInfo(json.data?.score_info || [])
      setScore(json.data?.series_info?.score || 0)
      setReviewCountInfo(json.data?.review_count_info)
      setAuthors(json.data?.authors || [])
    }
    try {
      fetchData()
    } catch (err) {
      console.error(err)
    }
  }, [series_id])

  const sortedScoreInfo = useMemo(
    () =>
      ['外观', '动力', '操控', '舒适性', '空间', '配置', '内饰'].map(label => ({
        name: label,
        value: scoreInfo.find(ele => ele.name === label)?.value,
      })),
    [scoreInfo]
  )

  const remark = useMemo(() => {
    if (!score) {
      return '暂无评分'
    }
    if (score < 1) {
      return '极差'
    } else if (score < 2) {
      return '较差'
    } else if (score < 3) {
      return '一般'
    } else if (score < 4) {
      return '良好'
    } else if (score <= 5) {
      return '优秀'
    }
  }, [score])

  const option = useMemo(() => {
    if (scoreInfo.length === 0) {
      return
    }
    const [
      { value: appearance_score },
      { value: power_score },
      { value: control_score },
      { value: comfort_score },
      { value: space_score },
      { value: configuration_score },
      { value: interiors_score },
    ] = scoreInfo
    const MAX = 5
    return {
      title: {
        text: score ? (score / 100).toFixed(2) : '暂无评分',
        x: 'center',
        y: 'center',
        textStyle: {
          color: '#606370',
          lineHeight: '18px',
          fontSize: 10,
        },
      },
      radar: {
        // shape: 'circle',
        center: ['50%', '50%'],
        indicator: scoreInfo.map(scoreInfoItem => ({
          name: `${scoreInfoItem.name}`,
          max: MAX,
        })),
        radius: 45,
        splitNumber: 3,
        silent: true,
        nameGap: 13,
        shape: 'circle',
        splitArea: {
          areaStyle: {
            color: '#f2f4fa',
          },
        },
        name: {
          textStyle: {
            color: '#1f2129',
            fontSize: 10,
            lineHeight: 14,
          },
          show: false,
        },
        splitLine: {
          lineStyle: {
            color: '#c9cbd6',
            width: 0.6,
          },
        },
        axisLine: {
          lineStyle: {
            color: '#c9cbd6',
            width: 0.6,
          },
        },
      },
      series: [
        {
          type: 'radar',
          symbol: 'none',
          lineStyle: {
            color: 'rgba(255, 206, 52, 100)',
          },
          areaStyle: {
            normal: {
              color: {
                type: 'radial',
                x: 0.5,
                y: 0.54,
                r: 0.5,
                colorStops: [
                  {
                    color: 'rgba(255, 208, 66, 60)',
                    offset: 0,
                  },
                  {
                    color: 'rgba(255, 118, 36, 100)',
                    offset: 1,
                  },
                ],
              },
            },
          },

          data: [
            {
              value: [
                appearance_score,
                interiors_score,
                configuration_score,
                space_score,
                comfort_score,
                control_score,
                power_score,
              ],
            },
          ],
        },
      ],
    }
  }, [scoreInfo, score])

  const ratios = useMemo(() => {
    const {
      range1 = 0,
      range2 = 0,
      range3 = 0,
      range4 = 0,
      range5 = 0,
      total = 0,
    } = reviewCountInfo || {}
    const ratios = [
      ((range1 / total) * 100).toFixed(0),
      ((range2 / total) * 100).toFixed(0),
      ((range3 / total) * 100).toFixed(0),
      ((range4 / total) * 100).toFixed(0),
      ((range5 / total) * 100).toFixed(0),
    ]
    return ratios.map(ratio =>
      Number.isNaN(Number(ratio)) ? 0 : Number(ratio)
    )
  }, [reviewCountInfo])

  const [seriesType, setSeriesType] = useState('')
  const [rankItems, setRankItems] = useState<IRankItem[]>([])

  useEffect(() => {
    const fetchData = async () => {
      const query = {
        series_id,
      }
      const json = await fetch(
        `/motor/m_api/pc_compared_series_rank?${stringify(query)}`
      ).catch(e => {
        // no-op
      })
      setRankItems(json?.data?.list || [])
      setSeriesType(json?.data?.series_type)
    }
    fetchData()
  }, [])

  return score > 0 ? (
    <div className={classNames('dcar-score', className)}>
      <img className="point-wave" src={wave_point_icon} />
      <div className="title">
        <img src={dcar_score_icon} className="dcar-score-icon" />
        <p className="desc">每次评分都源自一次真实的体验</p>
      </div>

      <div className="score-overall">
        <span className="score">{score}</span>
        <span className="unit">分</span>
        <span className="remark">&ldquo;{remark}&rdquo;</span>
      </div>
      <div className="stars">
        <Stars
          starIcon={star_icon}
          starVacantIcon={star_modest_icon}
          maxScore={5}
          starWidth={13}
          starHeight={12}
          score={score}
        />
      </div>
      <div className="statistic">
        <span className="range">4分-5分</span>
        <span className="bar">
          <Bar color="#ffcc32" colorVacant="#f2f4fa" percent={ratios[4]} />
        </span>
        <span className="ratio">{ratios[4]}%</span>
        <span className="range">3分-4分</span>
        <span className="bar">
          <Bar color="#ffcc32" colorVacant="#f2f4fa" percent={ratios[3]} />
        </span>
        <span className="ratio">{ratios[3]}%</span>
        <span className="range">2分-3分</span>
        <span className="bar">
          <Bar color="#ffcc32" colorVacant="#f2f4fa" percent={ratios[2]} />
        </span>
        <span className="ratio">{ratios[2]}%</span>
        <span className="range">1分-2分</span>
        <span className="bar">
          <Bar color="#ffcc32" colorVacant="#f2f4fa" percent={ratios[1]} />
        </span>
        <span className="ratio">{ratios[1]}%</span>
        <span className="range">0分-1分</span>
        <span className="bar">
          <Bar color="#ffcc32" colorVacant="#f2f4fa" percent={ratios[0]} />
        </span>
        <span className="ratio">{ratios[0]}%</span>
      </div>
      {authors.length > 0 && (
        <div className="review-count-wrapper">
          <span className="authors">
            {authors.map(author => (
              <img
                src={author.avatar_url}
                className="avatar"
                key={author.user_id}
              />
            ))}
          </span>
          <span className="total-review">
            共{reviewCountInfo?.total || 0}人评分
          </span>
        </div>
      )}
      {option && (
        <>
          <RadarChart option={option} className="radar-chart" />
          {sortedScoreInfo.map(({ name, value }, index) => {
            let align = 'left'
            if (index === 0) {
              align = 'center'
            } else if (index <= 3) {
              align = 'right'
            } else {
              align = 'left'
            }
            return (
              <React.Fragment key={index}>
                <Indicator
                  align={align as any}
                  value={value}
                  name={name}
                  className={`indicator-${index}`}
                />
              </React.Fragment>
            )
          })}
        </>
      )}
      {rankItems.length > 0 && (
        <div>
          <div className="split-line" />
          <SeriesRank rankItems={rankItems} seriesType={seriesType} />
        </div>
      )}

      <style jsx>
        {`
          .dcar-score {
            position: relative;
            width: 320px;
            height: ${rankItems.length <= 0 ? '224px' : '489px'};
            display: flex;
            flex-direction: column;
            padding: 18px 16px;
            background-color: white;
          }
          .point-wave {
            width: 160px;
            height: 75px;
            object-fit: cover;
            object-position: center;
            position: absolute;
            top: 0;
            right: 0;
          }
          .title {
            display: flex;
            align-items: center;
          }

          .dcar-score-icon {
            width: 57px;
            height: 22px;
            object-fit: cover;
          }
          .desc {
            margin-left: 8px;
            font-size: 12px;
            line-height: 1.5;
            color: #979aa8;
          }
          .score-overall {
            margin-top: 24px;
            display: flex;
            align-items: baseline;
          }
          .score {
            font-family: DINAlternate;
            font-size: 30px;
            font-weight: bold;
            color: #1f2129;
          }
          .unit {
            font-size: 12px;
            font-weight: 500;
            line-height: 1.5;
            color: #1f2129;
            font-family: PingFangSC-Medium;
            margin-left: 4px;
          }
          .remark {
            font-size: 12px;
            line-height: 1.5;
            color: #979aa8;
            margin-left: 16px;
          }
          .stars {
            margin-top: 8px;
            width: 65px;
            height: 12px;
          }
          .statistic {
            margin-top: 15px;
            display: grid;
            grid-template-rows: repeat(5, 8px);
            grid-template-columns: 31px 63px 22px;
            gap: 4px 4px;
            align-items: center;
            justify-items: start;
          }
          .range,
          .ratio {
            color: #606370;
            font-size: 12px;
            line-height: 8px;
            transform: scale(0.666);
            transform-origin: left center;
            white-space: nowrap;
          }
          .bar {
            height: 4px;
            width: 100%;
          }
          .review-count-wrapper {
            display: flex;
            height: 18px;
            align-items: center;
            margin-top: 14px;
          }
          .authors {
            width: 30px;
            display: flex;
            align-items: center;
          }
          .avatar {
            object-fit: cover;
            width: 14px;
            height: 14px;
            margin-right: -8px;
            border-radius: 50%;
          }
          .total-review {
            margin-left: 4px;
            color: #979aa8;
            font-size: 12px;
            line-height: 18px;
          }
          .split-line {
            height: 1px;
            background-color: #f2f4fa;
            margin-top: 10px;
          }

          :global(.radar-chart) {
            position: absolute;
            left: 187px;
            top: 90px;
            width: 90px;
            height: 90px;
          }
          :global(.indicator-0) {
            position: absolute;
            top: 57px;
            left: 220px;
          }
          :global(.indicator-1) {
            position: absolute;
            top: 86px;
            left: 167px;
          }
          :global(.indicator-2) {
            position: absolute;
            top: 141px;
            left: 160px;
          }
          :global(.indicator-3) {
            position: absolute;
            top: 182px;
            left: 182px;
          }
          :global(.indicator-4) {
            position: absolute;
            top: 182px;
            left: 254px;
          }
          :global(.indicator-5) {
            position: absolute;
            left: 280px;
            top: 141px;
          }
          :global(.indicator-6) {
            position: absolute;
            top: 86px;
            left: 274px;
          }
        `}
      </style>
    </div>
  ) : null
}
